import { useMutation, type UseMutationOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  addUsersToUsergroup,
  addUserToUsergroup,
  createUsergroup,
  deleteUsergroup,
  getSearchUser,
  getSearchUsers,
  getUsergroup,
  getUsergroups,
  removeUserFromUsergroup,
  renameUsergroup,
  UsergroupApiKeysEnum,
} from '~services/api/calls/user-management';
import { ApiErrorDescriptor } from '~services/api/helpers';

export const useUsergroup = (groupName: string, options?: UseQueryOptionsBase<UsergroupShape, ApiErrorDescriptor>) =>
  useQuery<UsergroupShape, ApiErrorDescriptor>({
    queryKey: [UsergroupApiKeysEnum.GetUsergroup, groupName],
    queryFn: async () => await getUsergroup(groupName),
    staleTime: 3 * 60 * 1000, // cache for 3 minutes
    refetchOnWindowFocus: false,
    ...options,
  });

export const useUsergroups = (options?: UseQueryOptionsBase<UsergroupShape[], ApiErrorDescriptor>) =>
  useQuery({
    queryKey: [UsergroupApiKeysEnum.GetUsergroups],
    queryFn: getUsergroups,
    staleTime: 3 * 60 * 1000, // cache for 3 minutes
    refetchOnWindowFocus: false,
    ...options,
  });

export const useDeleteUsergroup = (options?: UseMutationOptions<UsergroupPatchDto, ApiErrorDescriptor, string>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteUsergroup,
    ...options,
    onSuccess: (data, variables, context) => {
      if (options.onSuccess) {
        void options.onSuccess(data, variables, context);
      }
      setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: [UsergroupApiKeysEnum.GetUsergroups] });
      }, 1000);
    },
  });
};

export const useRenameUsergroup = (
  options?: UseMutationOptions<
    UsergroupPatchDto,
    ApiErrorDescriptor,
    { groupName: string; newGroupName: string },
    unknown
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: renameUsergroup,
    ...options,
    onSuccess: (data, variables, context) => {
      void queryClient.invalidateQueries({ queryKey: [UsergroupApiKeysEnum.GetUsergroups] });
      void queryClient.invalidateQueries({ queryKey: [UsergroupApiKeysEnum.GetUsergroup, variables.groupName] });
      if (options.onSuccess) {
        void options.onSuccess(data, variables, context);
      }
    },
  });
};

export const useAddUserToUsergroup = (
  options?: UseMutationOptions<UsergroupPatchDto, ApiErrorDescriptor, { groupName: string; userName: string }>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addUserToUsergroup,
    ...options,
    onSuccess: (data, variables, context) => {
      void queryClient.invalidateQueries({
        queryKey: [UsergroupApiKeysEnum.GetUsergroup, variables.groupName],
        refetchType: 'all',
      });
      if (options.onSuccess) {
        void options.onSuccess(data, variables, context);
      }
    },
  });
};

export const useAddUsersToUsergroup = (
  options?: UseMutationOptions<UsergroupPatchDto[], ApiErrorDescriptor, { groupName: string; userNames: string[] }>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addUsersToUsergroup,
    ...options,
    onSuccess: (data, variables, context) => {
      void queryClient.invalidateQueries({
        queryKey: [UsergroupApiKeysEnum.GetUsergroup, variables.groupName],
        refetchType: 'all',
      });
      if (options.onSuccess) {
        void options.onSuccess(data, variables, context);
      }
    },
  });
};

export const useSearchUser = (
  searchTerm: string,
  options?: UseQueryOptionsBase<UserSearchResult, ApiErrorDescriptor>,
) =>
  useQuery({
    queryKey: [UsergroupApiKeysEnum.SearchUser, searchTerm],
    queryFn: async () => await getSearchUser(searchTerm),
    enabled: !!searchTerm && searchTerm.length > 2,
    retry: false,
    refetchOnWindowFocus: false,
    ...options,
  });

export const useSearchUsers = (
  searchTerm: string,
  options?: UseQueryOptionsBase<UserSearchResult, ApiErrorDescriptor>,
) =>
  useQuery({
    queryKey: [UsergroupApiKeysEnum.SearchUsers, searchTerm],
    queryFn: async () => await getSearchUsers(searchTerm),
    enabled: !!searchTerm && searchTerm.length > 2,
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    ...options,
  });

export const useCreateUsergroup = (
  options?: UseMutationOptions<UsergroupPatchDto, ApiErrorDescriptor, UsergroupDto>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createUsergroup,
    ...options,
    onSuccess: (data, variables, context) => {
      void queryClient.invalidateQueries({ queryKey: [UsergroupApiKeysEnum.GetUsergroups] });
      if (options.onSuccess) {
        void options.onSuccess(data, variables, context);
      }
    },
  });
};

export const useRemoveUserFromUsergroup = (
  options?: UseMutationOptions<
    UsergroupPatchDto,
    ApiErrorDescriptor,
    {
      groupName: string;
      userName: string;
    }
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeUserFromUsergroup,
    ...options,
    onSuccess: (data, variables, context) => {
      setTimeout(() => {
        void queryClient.invalidateQueries({
          queryKey: [UsergroupApiKeysEnum.GetUsergroup, variables.groupName],
          refetchType: 'all',
        });
      }, 1000);
      if (options.onSuccess) {
        void options.onSuccess(data, variables, context);
      }
    },
  });
};
